import type { GlobalState } from '@/redux/store';

import { setAuthToken, setUserId } from '@/network/trackingContext.client';
import { getAuthToken, getBidderId } from '@/redux/modules/account/user/user.selectors';

// Only the two store members this needs, so a spec can drive it without a full store.
type TrackingContextStore = {
    getState: () => GlobalState;
    subscribe: (listener: () => void) => () => void;
};

const getSyncedUserId = (state: GlobalState): string | null => {
    const bidderId = getBidderId(state);
    return bidderId > 0 ? String(bidderId) : null;
};

/**
 * search-party requests are assembled in `redux/api/helpers.ts`, which has no access to
 * the redux store and therefore reads the auth token and userId from the module-level
 * tracking context. Mirror the store into that context on every change so a login or
 * logout without a full page reload is reflected on the very next request.
 *
 * deviceId is deliberately not mirrored: it is the stable anonymous Amplitude device
 * cookie and does not change with the session.
 *
 * Browser-only. It pulls in `trackingContext.client`, which keeps its state in a
 * module-level variable; importing this from anything that also runs on the server
 * would share one token across concurrent requests.
 *
 * Returns the store's unsubscribe function.
 */
export const syncTrackingContextWithStore = (store: TrackingContextStore): (() => void) => {
    const sync = () => {
        const state = store.getState();
        // getAuthToken returns '' for a missing, malformed or expired token, so an expired
        // session stops sending the header without needing an explicit expiry check here.
        // Normalized to null so "no token" has one shape here and on the SSR side.
        setAuthToken(getAuthToken(state) || null);
        setUserId(getSyncedUserId(state));
    };

    sync();

    return store.subscribe(sync);
};
